import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type {
  SetSessionWebhookInput,
  WhatsappConnection,
  WhatsappProfile,
  WhatsappSession,
} from "@shared/api";
import {
  useCreateWhatsappSession,
  useDeleteWhatsappSession,
  useRenameWhatsappSession,
  useRotateWhatsappSessionToken,
  useSetWhatsappSessionWebhook,
  useWhatsappSessionConnect,
  useWhatsappSessionLogs,
  useWhatsappSessionLogout,
  useWhatsappSessions,
} from "@client/api/queries";
import { ConfirmDialog } from "@client/components/ConfirmDialog";
import { DocsDialog } from "@client/components/DocsDialog";
import { toast } from "@client/components/toast";
import { useWhatsappSocket } from "@client/hooks/useWhatsappSocket";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Error inesperado";
}

const CONNECTION_LABEL: Record<WhatsappConnection, string> = {
  idle: "Inactivo",
  connecting: "Conectando…",
  open: "Conectado",
  close: "Desconectado",
  logged_out: "Sesión cerrada",
};

const BADGE_CLASS: Record<WhatsappConnection, string> = {
  idle: "badge-ghost",
  connecting: "badge-warning",
  open: "badge-success",
  close: "badge-error",
  logged_out: "badge-error",
};

const STATUS_CLASS: Record<WhatsappConnection, string> = {
  idle: "status",
  connecting: "status status-warning animate-pulse",
  open: "status status-success",
  close: "status status-error",
  logged_out: "status status-error",
};

function platformLabel(platform: string | null): string {
  if (!platform) return "—";
  const value = platform.toLowerCase();
  if (value.includes("android")) return "Android";
  if (value.includes("ios") || value.includes("iphone")) return "iPhone (iOS)";
  if (value.includes("smba") || value.includes("smbi")) return "Teléfono principal";
  if (value.includes("web")) return "WhatsApp Web";
  if (value.includes("desktop")) return "Desktop";
  if (value.includes("windows")) return "Windows";
  if (value.includes("mac")) return "macOS";
  return platform;
}

function shortJid(jid: string | null): string {
  if (!jid) return "—";
  return jid.split("@")[0] ?? jid;
}

type AvatarSize = "sm" | "md" | "lg";
const AVATAR_SIZE: Record<AvatarSize, string> = {
  sm: "h-10 w-10 text-sm",
  md: "h-14 w-14 text-lg",
  lg: "h-20 w-20 text-2xl",
};

function Avatar({
  name,
  url,
  size = "sm",
}: {
  name: string | null;
  url: string | null;
  size?: AvatarSize;
}) {
  const classes = AVATAR_SIZE[size];
  if (url) {
    return (
      <img
        src={url}
        alt={name ?? "Perfil"}
        className={`shrink-0 rounded-full object-cover ring ring-primary/40 ${classes}`}
      />
    );
  }
  const initials = (name ?? "?").trim().slice(0, 2).toUpperCase() || "?";
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-primary/90 font-semibold text-primary-content ${classes}`}
    >
      {initials}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-base-300/70 bg-base-100 px-3 py-2 transition-colors duration-200 hover:border-primary/40">
      <p className="text-[10px] tracking-widest text-base-content/40 uppercase">{label}</p>
      <p className="mt-0.5 truncate text-sm font-medium">{value}</p>
    </div>
  );
}

function CopyField({
  label,
  value,
  secret = false,
}: {
  label: string;
  value: string;
  secret?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // clipboard no disponible
    }
  }

  const hidden = "•".repeat(Math.min(value.length || 8, 28));
  const shown = secret && !revealed ? hidden : value;

  return (
    <div>
      <p className="mb-1 text-[10px] tracking-widest text-base-content/40 uppercase">{label}</p>
      <div className="join w-full">
        <input
          readOnly
          value={shown}
          onFocus={(event) => event.currentTarget.select()}
          className="input input-bordered join-item w-full font-mono text-xs"
        />
        {secret ? (
          <button
            type="button"
            onClick={() => setRevealed((current) => !current)}
            className="btn join-item"
            title={revealed ? "Ocultar" : "Mostrar"}
          >
            {revealed ? "Ocultar" : "Ver"}
          </button>
        ) : null}
        <button type="button" onClick={copy} className="btn join-item btn-primary">
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

function ConnectionBadge({ connection }: { connection: WhatsappConnection }) {
  return (
    <span className={`badge gap-2 ${BADGE_CLASS[connection]}`}>
      <span className={STATUS_CLASS[connection]} />
      {CONNECTION_LABEL[connection]}
    </span>
  );
}

function WebhookEditor({
  session,
  saving,
  onSave,
  onClear,
}: {
  session: WhatsappSession;
  saving: boolean;
  onSave: (input: SetSessionWebhookInput) => void;
  onClear: () => void;
}) {
  const [url, setUrl] = useState(session.webhookUrl ?? "");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState(session.webhookEvents.join(", "));
  const configured = Boolean(session.webhookUrl);

  function save() {
    const trimmed = url.trim();
    if (!trimmed) {
      onClear();
      return;
    }
    const parsedEvents = events
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    onSave({
      url: trimmed,
      secret: secret.trim() ? secret.trim() : undefined,
      events: parsedEvents.length > 0 ? parsedEvents : undefined,
    });
  }

  return (
    <section className="card card-lift animate-fade-in-up bg-base-100 shadow-xl">
      <div className="card-body gap-3">
        <div className="flex items-center justify-between">
          <h2 className="card-title text-base">Webhook de la sesión</h2>
          <span className={configured ? "badge badge-success" : "badge badge-ghost"}>
            {configured ? "Propio" : "Global"}
          </span>
        </div>
        <div>
          <p className="mb-1 text-[10px] tracking-widest text-base-content/40 uppercase">
            URL del webhook
          </p>
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://n8n.midominio.com/webhook/whatsapp"
            className="input input-bordered w-full font-mono text-xs"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-[10px] tracking-widest text-base-content/40 uppercase">
              Secreto HMAC {session.webhookSecretSet ? "(configurado)" : "(usa el global)"}
            </p>
            <input
              type="password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              placeholder={session.webhookSecretSet ? "•••• (vacío = conservar)" : "opcional"}
              className="input input-bordered w-full font-mono text-xs"
            />
          </div>
          <div>
            <p className="mb-1 text-[10px] tracking-widest text-base-content/40 uppercase">
              Eventos (vacío = global)
            </p>
            <input
              value={events}
              onChange={(event) => setEvents(event.target.value)}
              placeholder="message, status, action"
              className="input input-bordered w-full font-mono text-xs"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={save} disabled={saving} className="btn btn-primary btn-sm">
            {saving ? <span className="loading loading-spinner loading-xs" /> : null}
            Guardar webhook
          </button>
          <button
            type="button"
            onClick={onClear}
            disabled={saving || !configured}
            className="btn btn-outline btn-sm"
          >
            Quitar (usar global)
          </button>
        </div>
        <p className="text-xs text-base-content/40">
          Sin webhook propio, la sesión usa el global. La firma llega en{" "}
          <code className="font-mono">X-Webhook-Signature</code> y el id en{" "}
          <code className="font-mono">X-Webhook-Session</code>.
        </p>
      </div>
    </section>
  );
}

function SessionItem({
  session,
  active,
  onSelect,
}: {
  session: WhatsappSession;
  active: boolean;
  onSelect: () => void;
}) {
  const profile = session.status.profile;
  const name = profile?.name ?? session.label;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-200 hover:translate-x-0.5 ${
        active
          ? "border-primary/60 bg-primary/10 shadow-sm"
          : "border-base-300/60 bg-base-100 hover:border-primary/40"
      }`}
    >
      <Avatar name={name} url={profile?.profilePicUrl ?? null} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{session.label}</span>
        <span className="block truncate text-xs text-base-content/50">
          {profile ? `${name} · ${shortJid(profile.phoneNumber ?? profile.id)}` : "Sin vincular"}
        </span>
      </span>
      <span className={STATUS_CLASS[session.status.connection]} />
    </button>
  );
}

function ProfilePanel({ profile }: { profile: WhatsappProfile }) {
  return (
    <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
      <Avatar name={profile.name} url={profile.profilePicUrl} size="lg" />
      <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Nombre" value={profile.name ?? "—"} />
        <Stat label="Número" value={profile.phoneNumber ?? shortJid(profile.id)} />
        <Stat label="Dispositivo" value={platformLabel(profile.platform)} />
        <Stat
          label="ID dispositivo"
          value={profile.deviceId === null ? "—" : `#${profile.deviceId}`}
        />
        <Stat label="Registrado" value={profile.registered ? "Sí" : "No"} />
        <Stat label="LID" value={profile.lid ? shortJid(profile.lid) : "—"} />
      </div>
    </div>
  );
}

export function WhatsappPage({ onLogout }: { onLogout: () => void }) {
  const socket = useWhatsappSocket();
  const sessionsQuery = useWhatsappSessions(socket.connected);
  const create = useCreateWhatsappSession();
  const remove = useDeleteWhatsappSession();
  const rename = useRenameWhatsappSession();
  const rotate = useRotateWhatsappSessionToken();
  const connect = useWhatsappSessionConnect();
  const logout = useWhatsappSessionLogout();
  const setWebhook = useSetWhatsappSessionWebhook();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  const sessions = socket.sessions.length > 0 ? socket.sessions : (sessionsQuery.data ?? []);
  const selected = sessions.find((session) => session.id === selectedId) ?? sessions[0] ?? null;
  const key = selected?.id ?? null;
  // Remonta el editor solo cuando el backend confirma un cambio (no al teclear).
  const webhookKey = selected
    ? `${selected.id}:${selected.webhookUrl ?? ""}:${selected.webhookEvents.join(",")}:${selected.webhookSecretSet}`
    : "";

  const logsQuery = useWhatsappSessionLogs(key, socket.connected);
  const liveLogs = key ? (socket.logs[key] ?? []) : [];
  const liveIds = new Set(liveLogs.map((entry) => entry.id));
  const logs = [
    ...liveLogs,
    ...(logsQuery.data ?? []).filter((entry) => !liveIds.has(entry.id)),
  ].slice(0, 60);

  const status = selected?.status;
  const profile = status?.profile ?? null;
  const isOpen = status?.connection === "open";
  const showQr = !isOpen && Boolean(status?.qr);
  const anyPending =
    connect.isPending ||
    logout.isPending ||
    remove.isPending ||
    create.isPending ||
    rename.isPending ||
    rotate.isPending ||
    setWebhook.isPending;

  function onCreate() {
    const label = newLabel.trim();
    if (!label) return;
    create.mutate(
      { label },
      {
        onSuccess: (session) => {
          setNewLabel("");
          setCreating(false);
          setSelectedId(session.id);
          toast({ variant: "success", title: "Sesión creada", description: session.label });
        },
        onError: (error) =>
          toast({ variant: "error", title: "No se pudo crear", description: errorMessage(error) }),
      },
    );
  }

  function saveLabel() {
    if (!selected) return;
    const label = labelDraft.trim();
    setEditingLabel(false);
    if (!label || label === selected.label) return;
    rename.mutate(
      { id: selected.id, input: { label } },
      {
        onSuccess: () =>
          toast({ variant: "success", title: "Nombre actualizado", description: label }),
        onError: (error) =>
          toast({
            variant: "error",
            title: "No se pudo renombrar",
            description: errorMessage(error),
          }),
      },
    );
  }

  function onDelete() {
    if (!selected) return;
    setConfirmDelete(true);
  }

  function confirmDeleteNow() {
    if (!selected) return;
    remove.mutate(selected.id, {
      onSuccess: () => {
        setSelectedId(null);
        setConfirmDelete(false);
        toast({ variant: "success", title: "Sesión eliminada" });
      },
      onError: (error) => {
        setConfirmDelete(false);
        toast({ variant: "error", title: "No se pudo eliminar", description: errorMessage(error) });
      },
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-base-200 md:flex-row">
      {anyPending ? (
        <div className="fixed inset-x-0 top-0 z-[70] h-0.5 overflow-hidden bg-primary/20">
          <div className="animate-progress h-full w-1/3 rounded-full bg-primary" />
        </div>
      ) : null}

      {/* ── Sidebar ── */}
      <aside className="animate-fade-in flex w-full shrink-0 flex-col gap-3 border-b border-base-300 bg-base-100 p-4 md:h-screen md:w-80 md:border-r md:border-b-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold tracking-widest text-primary uppercase">
              Baileys
            </p>
            <h1 className="text-lg font-bold">Sesiones</h1>
          </div>
          <span className="badge badge-neutral">{sessions.length}</span>
        </div>

        <button
          type="button"
          onClick={() => setCreating((value) => !value)}
          className="btn btn-primary btn-sm transition-transform duration-200 hover:scale-[1.02] active:scale-95"
        >
          {creating ? "Cancelar" : "+ Nueva sesión"}
        </button>

        {creating ? (
          <div className="animate-fade-in flex gap-2">
            <input
              autoFocus
              value={newLabel}
              onChange={(event) => setNewLabel(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && onCreate()}
              placeholder="Nombre (ej. Ventas)"
              className="input input-bordered input-sm min-w-0 flex-1"
            />
            <button
              type="button"
              onClick={onCreate}
              disabled={create.isPending || !newLabel.trim()}
              className="btn btn-sm"
            >
              {create.isPending ? <span className="loading loading-spinner loading-xs" /> : "Crear"}
            </button>
          </div>
        ) : null}

        <div className="stagger flex flex-1 flex-col gap-2 overflow-y-auto">
          {sessionsQuery.isLoading && sessions.length === 0
            ? [0, 1, 2].map((index) => (
                <div key={index} className="skeleton-shimmer h-16 rounded-xl" />
              ))
            : null}
          {!sessionsQuery.isLoading && sessions.length === 0 ? (
            <p className="rounded-xl border border-dashed border-base-300 px-3 py-6 text-center text-sm text-base-content/40">
              No hay sesiones. Crea una y escanea el QR.
            </p>
          ) : null}
          {sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              active={session.id === key}
              onSelect={() => setSelectedId(session.id)}
            />
          ))}
        </div>

        <div className="flex items-center justify-between text-[11px] text-base-content/40">
          <span className="inline-flex items-center gap-1.5">
            <span className={socket.connected ? "status status-success" : "status"} />
            {socket.connected ? "en vivo" : "sin conexión"}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDocsOpen(true)}
              className="link link-hover transition-colors duration-200 hover:text-primary"
            >
              Documentación
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="link link-hover transition-colors duration-200 hover:text-primary"
            >
              Salir
            </button>
          </div>
        </div>
      </aside>

      {/* ── Detalle ── */}
      <main className="flex-1 p-4 md:h-screen md:overflow-y-auto md:p-8">
        {!selected || !status ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-base-content/40">
            <div className="animate-float flex h-14 w-14 items-center justify-center rounded-2xl border border-base-300 bg-base-100 shadow-lg">
              <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden>
                <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 2a8 8 0 1 1-4.2 14.8l-.4-.2-2.6.7.7-2.5-.2-.4A8 8 0 0 1 12 4Zm-3.2 4.2c-.2 0-.5 0-.7.3-.2.3-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.7 2.8 4.2 3.8 2.1.8 2.5.7 3 .6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.6-.4l-1.8-.9c-.2-.1-.4-.1-.6.1l-.6.8c-.1.2-.2.2-.4.1-.2-.1-.9-.3-1.7-1.1-.6-.6-1-1.2-1.1-1.4-.1-.2 0-.3.1-.4l.4-.5c.1-.2.1-.3.1-.5v-.5c-.2-.5-.7-1.4-.9-1.9-.1-.4-.3-.4-.5-.4h-.1Z" />
              </svg>
            </div>
            <p className="max-w-xs text-center text-sm">
              Selecciona o crea una sesión para vincular un dispositivo.
            </p>
          </div>
        ) : (
          <div key={selected.id} className="mx-auto flex max-w-3xl flex-col gap-6">
            <header className="animate-fade-in-up flex flex-wrap items-center gap-4">
              <Avatar
                name={profile?.name ?? selected.label}
                url={profile?.profilePicUrl ?? null}
                size="md"
              />
              <div className="min-w-0 flex-1">
                {editingLabel ? (
                  <input
                    autoFocus
                    value={labelDraft}
                    onChange={(event) => setLabelDraft(event.target.value)}
                    onBlur={saveLabel}
                    onKeyDown={(event) => event.key === "Enter" && saveLabel()}
                    className="input input-bordered input-sm w-full text-lg font-bold"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setLabelDraft(selected.label);
                      setEditingLabel(true);
                    }}
                    className="text-left text-xl font-bold transition-colors duration-200 hover:text-primary"
                  >
                    {selected.label}
                  </button>
                )}
                <p className="truncate text-sm text-base-content/50">
                  {profile
                    ? `${profile.name ?? "Sin nombre"} · ${shortJid(profile.phoneNumber ?? profile.id)}`
                    : "Sin vincular"}
                </p>
              </div>
              <ConnectionBadge connection={status.connection} />
            </header>

            {isOpen && profile ? (
              <section className="card card-lift animate-fade-in-up bg-base-100 shadow-xl">
                <div className="card-body">
                  <ProfilePanel profile={profile} />
                </div>
              </section>
            ) : null}

            <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Mensajes" value={String(status.messageCount)} />
              <Stat label="Dispositivo" value={platformLabel(profile?.platform ?? null)} />
              <Stat
                label="Conectado"
                value={status.connectedAt ? new Date(status.connectedAt).toLocaleString() : "—"}
              />
              <Stat label="Última caída" value={status.lastDisconnectReason ?? "—"} />
            </section>

            <section className="card card-lift animate-fade-in-up bg-base-100 shadow-xl">
              <div className="card-body gap-3">
                <div className="flex items-center justify-between">
                  <h2 className="card-title text-base">Credenciales de la sesión</h2>
                  <button
                    type="button"
                    onClick={() =>
                      rotate.mutate(selected.id, {
                        onSuccess: () => toast({ variant: "success", title: "Token rotado" }),
                        onError: (error) =>
                          toast({
                            variant: "error",
                            title: "No se pudo rotar",
                            description: errorMessage(error),
                          }),
                      })
                    }
                    disabled={rotate.isPending}
                    className="btn btn-xs btn-outline transition-transform duration-200 active:scale-95"
                  >
                    {rotate.isPending ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : null}
                    Rotar token
                  </button>
                </div>
                <CopyField label="Session ID" value={selected.id} />
                <CopyField label="Token de la sesión" value={selected.token} secret />
                <p className="text-xs text-base-content/40">
                  Úsalo como <code className="font-mono">Authorization: Bearer …</code> para operar
                  solo esta sesión (n8n u otra integración).
                </p>
              </div>
            </section>

            <WebhookEditor
              key={webhookKey}
              session={selected}
              saving={setWebhook.isPending}
              onSave={(input) =>
                setWebhook.mutate(
                  { id: selected.id, input },
                  {
                    onSuccess: () => toast({ variant: "success", title: "Webhook guardado" }),
                    onError: (error) =>
                      toast({
                        variant: "error",
                        title: "No se pudo guardar el webhook",
                        description: errorMessage(error),
                      }),
                  },
                )
              }
              onClear={() =>
                setWebhook.mutate(
                  { id: selected.id, input: { url: null } },
                  {
                    onSuccess: () =>
                      toast({
                        variant: "info",
                        title: "Webhook quitado",
                        description: "La sesión usará el global",
                      }),
                    onError: (error) =>
                      toast({
                        variant: "error",
                        title: "No se pudo quitar el webhook",
                        description: errorMessage(error),
                      }),
                  },
                )
              }
            />

            {showQr ? (
              <section className="card card-lift animate-pop bg-base-100 shadow-xl">
                <div className="card-body items-center gap-4">
                  <h2 className="card-title text-base">Escanea para vincular</h2>
                  <div className="rounded-2xl bg-white p-4 shadow-inner ring-1 ring-base-300 transition-transform duration-300 hover:scale-[1.02]">
                    <QRCodeSVG value={status.qr ?? ""} size={244} level="M" fgColor="#0b1220" />
                  </div>
                  <p className="max-w-sm text-center text-sm text-base-content/50">
                    WhatsApp → Dispositivos vinculados → Vincular un dispositivo.
                  </p>
                </div>
              </section>
            ) : null}

            {status.connection === "connecting" && !showQr ? (
              <p className="card animate-fade-in bg-base-100 p-6 text-center text-sm text-base-content/50 shadow-xl">
                <span className="loading loading-dots loading-sm" /> Estableciendo conexión…
              </p>
            ) : null}

            <section className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  connect.mutate(selected.id, {
                    onSuccess: () =>
                      toast({
                        variant: "info",
                        title: "Conectando…",
                        description: "Escanea el QR si aparece",
                      }),
                    onError: (error) =>
                      toast({
                        variant: "error",
                        title: "No se pudo conectar",
                        description: errorMessage(error),
                      }),
                  })
                }
                disabled={anyPending || isOpen}
                className="btn btn-primary transition-transform duration-200 hover:scale-[1.03] active:scale-95"
              >
                {connect.isPending ? <span className="loading loading-spinner loading-sm" /> : null}
                Conectar / nuevo QR
              </button>
              <button
                type="button"
                onClick={() =>
                  logout.mutate(selected.id, {
                    onSuccess: () => toast({ variant: "info", title: "Sesión cerrada" }),
                    onError: (error) =>
                      toast({
                        variant: "error",
                        title: "No se pudo cerrar sesión",
                        description: errorMessage(error),
                      }),
                  })
                }
                disabled={anyPending || status.connection === "idle"}
                className="btn btn-outline transition-transform duration-200 hover:scale-[1.03] active:scale-95"
              >
                {logout.isPending ? <span className="loading loading-spinner loading-sm" /> : null}
                Cerrar sesión
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={anyPending}
                className="btn btn-outline btn-error ml-auto transition-transform duration-200 hover:scale-[1.03] active:scale-95"
              >
                {remove.isPending ? <span className="loading loading-spinner loading-sm" /> : null}
                Eliminar sesión
              </button>
            </section>

            {create.isError ||
            connect.isError ||
            logout.isError ||
            remove.isError ||
            rename.isError ||
            setWebhook.isError ? (
              <div role="alert" className="alert alert-error animate-shake text-sm">
                <span>Algo falló. Revisa los logs de la sesión.</span>
              </div>
            ) : null}

            <section className="card card-lift animate-fade-in-up bg-base-100 shadow-xl">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <h2 className="card-title text-base">Actividad</h2>
                  <span className="text-xs text-base-content/40">eventos y logs</span>
                </div>
                <ul className="mt-2 flex max-h-80 flex-col gap-2 overflow-y-auto">
                  {logs.length === 0 ? (
                    <li className="text-sm text-base-content/40">Sin actividad todavía.</li>
                  ) : null}
                  {logs.map((entry) => (
                    <li
                      key={entry.id}
                      className="animate-fade-in rounded-lg border border-base-300/60 bg-base-200/60 px-3 py-2 text-xs transition-colors duration-200 hover:border-primary/30"
                    >
                      <div className="flex items-center justify-between text-base-content/40">
                        <span className="font-mono">{entry.event}</span>
                        <time>{new Date(entry.at).toLocaleTimeString()}</time>
                      </div>
                      {entry.detail ? (
                        <p className="mt-1 text-base-content/80">{entry.detail}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>
        )}
      </main>

      <ConfirmDialog
        open={confirmDelete}
        danger
        pending={remove.isPending}
        title="Eliminar sesión"
        description={
          selected
            ? `Se cerrará y borrará «${selected.label}» junto con sus credenciales. Esta acción no se puede deshacer.`
            : undefined
        }
        confirmLabel="Eliminar"
        onConfirm={confirmDeleteNow}
        onCancel={() => setConfirmDelete(false)}
      />

      <DocsDialog open={docsOpen} onClose={() => setDocsOpen(false)} />
    </div>
  );
}
